<script>
  import {
    Popup,
    Page,
    Navbar,
    NavRight,
    Link,
    Button,
    Icon,
    f7,
  } from "framework7-svelte";
  import { BASE_URL } from "../services/service.config";
  import { parsePayload, safeNumber } from "../js/audit-utils";

  export let show = false;
  export let environment = "LIVE";
  export let onClose = () => {};

  let loading = false;
  let reconData = null;
  let error = null;

  $: if (show) {
    loadReconData();
  }

  async function loadReconData() {
    loading = true;
    error = null;
    try {
      // Ingest latest recon state or query audit events for EOD_RECON
      const res = await fetch(`${BASE_URL}/api/v1/audit/events?event_type=EOD_RECON_VERIFIED&limit=5&environment=${environment}`);
      if (!res.ok) {
        throw new Error(`Failed to load EOD reconciliation status (HTTP ${res.status})`);
      }
      const data = await res.json();
      const latestVerified = data.events && data.events[0];
      const verifiedPayload = parsePayload(latestVerified?.payload_json);

      // Check for mismatch events
      const mismatchRes = await fetch(`${BASE_URL}/api/v1/audit/events?event_type=EOD_RECON_MISMATCH&limit=10&environment=${environment}`);
      const mismatchData = mismatchRes.ok ? await mismatchRes.json() : { events: [] };
      const mismatches = mismatchData.events || [];

      const matchedCount = safeNumber(verifiedPayload.matched_count ?? latestVerified?.fill_qty, 0);
      const turnover = safeNumber(verifiedPayload.total_turnover ?? latestVerified?.fill_price, 0);

      reconData = {
        status: mismatches.length === 0 ? "VERIFIED" : "MISMATCH",
        verifiedEvent: latestVerified,
        mismatches: mismatches,
        totalChecked: matchedCount + mismatches.length,
        totalMatched: matchedCount,
        mismatchCount: mismatches.length,
        lastReconTime: latestVerified?.created_at || new Date().toISOString(),
        turnoverVerified: turnover,
      };
    } catch (err) {
      console.error("Error loading recon status:", err);
      error = err.message || "Failed to query EOD reconciliation";
    } finally {
      loading = false;
    }
  }

  function exportReconReport() {
    const exportUrl = `${BASE_URL}/api/v1/audit/export?environment=${environment}&format=csv&event_type=EOD_RECON_VERIFIED`;
    window.open(exportUrl, '_blank');
    f7.toast.create({
      text: "📥 Initiated SEBI EOD Reconciliation Report CSV download",
      closeTimeout: 3000,
    }).open();
  }
</script>

<Popup opened={show} onPopupClosed={onClose} class="audit-recon-popup">
  <Page style="background: #0f172a; color: #e2e8f0;">
    <Navbar title="EOD Automated Reconciliation Inspector">
      <NavRight>
        <Link popupClose iconF7="xmark_circle_fill" />
      </NavRight>
    </Navbar>

  <div class="recon-container">
    {#if loading}
      <div class="loading-box">
        <div class="spinner"></div>
        <p>Verifying BSE Trade Book records against TRDR Audit Database...</p>
      </div>
    {:else if error}
      <div class="error-box">
        <strong>⚠️ Error:</strong> {error}
        <Button small fill color="blue" class="margin-top" onClick={loadReconData}>
          Retry
        </Button>
      </div>
    {:else if reconData}
      <!-- Status Card -->
      <div class="recon-status-card {reconData.status === 'VERIFIED' ? 'status-verified' : 'status-mismatch'}">
        <div class="status-left">
          <span class="status-badge-lg">
            {reconData.status === 'VERIFIED' ? '🟢 EOD RECON VERIFIED' : '🔴 EOD RECON MISMATCH DETECTED'}
          </span>
          <p class="status-desc">
            {reconData.status === 'VERIFIED' 
              ? 'All BSE exchange fills match 100% with local order execution database.'
              : `${reconData.mismatchCount} trade record discrepancies detected between BSE Trade Book and local database.`}
          </p>
          <span class="last-checked">Last Reconciliation: {new Date(reconData.lastReconTime).toLocaleString('en-IN')}</span>
        </div>
        <div class="status-right">
          <Button small fill color="green" onClick={exportReconReport}>
            <Icon f7="arrow_down_doc_fill" size="14" /> &nbsp; Export SEBI Report
          </Button>
        </div>
      </div>

      <!-- Aggregate Statistics Strip -->
      <div class="recon-metrics-grid">
        <div class="stat-card">
          <span class="stat-lbl">BSE Trade Records</span>
          <span class="stat-num text-blue">{reconData.totalChecked}</span>
        </div>
        <div class="stat-card">
          <span class="stat-lbl">Matched & Verified</span>
          <span class="stat-num text-green">{reconData.totalMatched}</span>
        </div>
        <div class="stat-card">
          <span class="stat-lbl">Discrepancies</span>
          <span class="stat-num {reconData.mismatchCount > 0 ? 'text-red font-bold' : 'text-slate'}">
            {reconData.mismatchCount}
          </span>
        </div>
        <div class="stat-card">
          <span class="stat-lbl">Total Turnover Verified</span>
          <span class="stat-num text-purple">
            ₹{reconData.turnoverVerified.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
      </div>

      <!-- Discrepancies Table -->
      <div class="recon-table-section">
        <div class="recon-table-header">
          <span>📋 Discrepancy Breakdown & Exception Log</span>
          <span class="disc-count">{reconData.mismatchCount} Exceptions</span>
        </div>

        {#if reconData.mismatches.length === 0}
          <div class="verified-empty">
            <Icon f7="checkmark_seal_fill" size="36" color="green" />
            <p>Zero discrepancies found. All exchange execution numbers, client UCCs, execution prices, and filled quantities are in perfect parity.</p>
          </div>
        {:else}
          <table class="recon-table">
            <thead>
              <tr>
                <th>Scrip / Symbol</th>
                <th>Client UCC</th>
                <th>Exchange Order ID</th>
                <th>BSE Filled Qty</th>
                <th>Local DB Qty</th>
                <th>Delta</th>
                <th>Discrepancy Details</th>
              </tr>
            </thead>
            <tbody>
              {#each reconData.mismatches as disc}
                <tr>
                  <td><strong>{disc.symbol}</strong></td>
                  <td><code>{disc.client_ucc}</code></td>
                  <td><code>{disc.exchange_order_no || '-'}</code></td>
                  <td>{disc.order_qty || 0}</td>
                  <td>{disc.fill_qty || 0}</td>
                  <td class="text-red font-bold">{(disc.order_qty || 0) - (disc.fill_qty || 0)}</td>
                  <td>{disc.trigger_reason || 'Quantity mismatch against broker trade log'}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        {/if}
      </div>
    {/if}
  </div>
  </Page>
</Popup>

<style>
  .audit-recon-popup {
    --f7-popup-tablet-width: 860px;
    --f7-popup-tablet-height: 85vh;
  }
  .recon-container {
    padding: 16px;
    background: #0f172a;
    color: #e2e8f0;
    min-height: calc(100vh - 60px);
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  }
  .loading-box {
    text-align: center;
    padding: 48px;
    color: #94a3b8;
  }
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #334155;
    border-top-color: #38bdf8;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin: 0 auto 16px;
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  .error-box {
    background: #7f1d1d;
    color: #fecaca;
    padding: 16px;
    border-radius: 8px;
  }

  .recon-status-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 8px;
    padding: 16px 20px;
    margin-bottom: 16px;
  }
  .status-verified {
    background: linear-gradient(135deg, rgba(6, 95, 70, 0.35) 0%, rgba(15, 23, 42, 0.95) 100%);
    border: 1px solid #059669;
  }
  .status-mismatch {
    background: linear-gradient(135deg, rgba(127, 29, 29, 0.35) 0%, rgba(15, 23, 42, 0.95) 100%);
    border: 1px solid #dc2626;
  }
  .status-badge-lg {
    font-size: 15px;
    font-weight: bold;
    letter-spacing: 0.5px;
  }
  .status-desc {
    margin: 6px 0;
    font-size: 13px;
    color: #cbd5e1;
  }
  .last-checked {
    font-size: 11px;
    color: #94a3b8;
  }

  .recon-metrics-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 10px;
    margin-bottom: 16px;
  }
  .stat-card {
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 6px;
    padding: 10px 14px;
    display: flex;
    flex-direction: column;
  }
  .stat-lbl {
    font-size: 10px;
    color: #64748b;
    text-transform: uppercase;
    margin-bottom: 4px;
  }
  .stat-num {
    font-size: 18px;
    font-weight: bold;
    color: #f8fafc;
  }

  .recon-table-section {
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 8px;
    overflow: hidden;
  }
  .recon-table-header {
    background: #0f172a;
    padding: 12px 16px;
    font-size: 13px;
    font-weight: 600;
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid #334155;
  }
  .disc-count {
    font-size: 11px;
    color: #94a3b8;
  }

  .verified-empty {
    text-align: center;
    padding: 40px 20px;
    color: #94a3b8;
  }
  .verified-empty p {
    margin-top: 12px;
    font-size: 13px;
    color: #cbd5e1;
  }

  .recon-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
  }
  .recon-table th {
    background: #1e293b;
    color: #94a3b8;
    text-align: left;
    padding: 10px 12px;
    border-bottom: 1px solid #334155;
  }
  .recon-table td {
    padding: 8px 12px;
    border-bottom: 1px solid #1e293b;
  }

  .text-green { color: #34d399 !important; }
  .text-red { color: #f87171 !important; }
  .text-blue { color: #38bdf8 !important; }
  .text-purple { color: #c084fc !important; }
  .text-slate { color: #94a3b8 !important; }
  .font-bold { font-weight: bold; }
</style>
